import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Description,
  CloudDownload,
  FolderZip,
  Edit as EditIcon,
  SyncAlt,
  CheckCircle,
  Close,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  Timestamp,
  orderBy,
} from 'firebase/firestore';
import { MainLayout } from '../components/layout/MainLayout';
import { db, auth } from '../lib/firebase';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay } from 'date-fns';
import { ko } from 'date-fns/locale';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import {
  CombinedRouteData,
  RouteData,
  RouteConfiguration,
  ManualPassengerCount,
  PassengerSummary,
} from '../types/report';
import { RouteMonthlyCheckin, CheckinRecord } from '../types/boarding';
import { Operation } from '../types/operation';

// 한글 요일 반환
const getKoreanWeekday = (date: Date): string => {
  const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
  return weekdays[getDay(date)];
};

// date-fns getDay는 0=일요일, 6=토요일 반환
// Firebase는 1=월요일, 7=일요일 사용
const convertToFirebaseWeekday = (date: Date): number => {
  const day = getDay(date); // 0-6 (0=일)
  return day === 0 ? 7 : day; // 1-7 (1=월, 7=일)
};

export const ReportPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [combinedData, setCombinedData] = useState<CombinedRouteData[]>([]);
  const [summary, setSummary] = useState<PassengerSummary>({
    hadan1Total: 0,
    hadan2Total: 0,
    gangseo1Total: 0,
    gangseo2Total: 0,
    totalPassengers: 0,
  });

  // 수기입력 다이얼로그
  const [manualInputDialog, setManualInputDialog] = useState<{
    open: boolean;
    routeId: string;
    routeName: string;
    currentValue: number | null;
  }>({
    open: false,
    routeId: '',
    routeName: '',
    currentValue: null,
  });
  const [manualInputValue, setManualInputValue] = useState<string>('');

  // 마이그레이션 관련
  const [migrationStatus, setMigrationStatus] = useState<{
    total: number;
    migrated: number;
  } | null>(null);
  const [migrationDialog, setMigrationDialog] = useState(false);
  const [migrating, setMigrating] = useState(false);

  // 월간 PDF 생성 진행
  const [monthlyPdfProgress, setMonthlyPdfProgress] = useState<{
    open: boolean;
    current: number;
    total: number;
  }>({ open: false, current: 0, total: 0 });

  // 일별 PDF 생성 로딩 상태
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // 권한 확인
  const canAccessMigration = (): boolean => {
    const user = auth.currentUser;
    if (!user) return false;
    const authorizedEmail = 'hi0067@icloud.com';
    return user.email?.toLowerCase() === authorizedEmail.toLowerCase();
  };

  // 마이그레이션 상태 확인
  const checkMigrationStatus = async () => {
    try {
      const routesSnapshot = await getDocs(collection(db, 'routes'));
      const total = routesSnapshot.size;

      const configsQuery = query(
        collection(db, 'route_configurations'),
        where('migrationSource', '==', 'auto_migration_from_routes')
      );
      const configsSnapshot = await getDocs(configsQuery);

      // 고유 routeId 개수 세기
      const migratedRouteIds = new Set<string>();
      configsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.routeId) {
          migratedRouteIds.add(data.routeId);
        }
      });

      setMigrationStatus({
        total,
        migrated: migratedRouteIds.size,
      });
    } catch (error) {
      console.error('마이그레이션 상태 확인 오류:', error);
    }
  };

  // 마이그레이션 실행
  const executeMigration = async () => {
    if (!canAccessMigration()) {
      alert('권한이 없습니다.');
      return;
    }

    setMigrating(true);
    try {
      console.log('=== routes → route_configurations 마이그레이션 시작 ===');
      console.log('실행자:', auth.currentUser?.email);

      const routesSnapshot = await getDocs(collection(db, 'routes'));
      console.log('총 노선 수:', routesSnapshot.size);

      const effectiveFrom = Timestamp.fromDate(new Date(2025, 0, 1)); // 2025-01-01
      const effectiveTo = Timestamp.fromDate(new Date(2025, 6, 31)); // 2025-07-31

      let successCount = 0;
      let skipCount = 0;

      for (const routeDoc of routesSnapshot.docs) {
        const routeId = routeDoc.id;
        const routeData = routeDoc.data() as RouteData;

        const configId = `${routeId}_20250101_20250731`;

        // 이미 존재하는지 확인
        const configDoc = await getDocs(
          query(collection(db, 'route_configurations'), where('__name__', '==', configId))
        );

        if (!configDoc.empty) {
          console.log(`${routeId}: 이미 마이그레이션됨 - 건너뛰기`);
          skipCount++;
          continue;
        }

        // 새 설정 문서 생성
        const newConfig: Omit<RouteConfiguration, 'id'> = {
          routeId,
          effectiveFrom,
          effectiveTo,
          name: routeData.name,
          departureTime: routeData.departureTime,
          operatingDays: routeData.operatingDays || [],
          description: routeData.description || '',
          createdAt: Timestamp.now(),
          migrationSource: 'auto_migration_from_routes',
          migrationDate: Timestamp.now(),
        };

        await setDoc(doc(db, 'route_configurations', configId), newConfig);
        console.log(`${routeId}: 마이그레이션 성공`);
        successCount++;
      }

      console.log('=== 마이그레이션 결과 ===');
      console.log('성공:', successCount);
      console.log('건너뛰기:', skipCount);
      console.log('=== 마이그레이션 완료 ===');

      alert(`마이그레이션 완료!\n성공: ${successCount}건\n건너뛰기: ${skipCount}건`);
      setMigrationDialog(false);
      await checkMigrationStatus();
    } catch (error) {
      console.error('마이그레이션 오류:', error);
      alert('마이그레이션 중 오류가 발생했습니다.');
    } finally {
      setMigrating(false);
    }
  };

  // 특정 날짜의 노선 설정 조회
  const getRouteConfigForDate = async (
    routeId: string,
    date: Date
  ): Promise<RouteConfiguration | null> => {
    try {
      const dateTimestamp = Timestamp.fromDate(date);

      const q = query(
        collection(db, 'route_configurations'),
        where('routeId', '==', routeId),
        where('effectiveFrom', '<=', dateTimestamp),
        orderBy('effectiveFrom', 'desc')
      );

      const snapshot = await getDocs(q);

      for (const doc of snapshot.docs) {
        const data = doc.data() as Omit<RouteConfiguration, 'id'>;
        const config = { id: doc.id, ...data };

        // effectiveTo가 null이거나 조회 날짜 이후인 경우
        if (config.effectiveTo === null || config.effectiveTo.toDate() >= date) {
          return config;
        }
      }

      return null;
    } catch (error) {
      console.error('노선 설정 조회 오류:', error);
      return null;
    }
  };

  // 이용인원 계산 (체크인 데이터 기반)
  const getPassengerCount = (checkins: CheckinRecord[], targetDate: Date): number => {
    return checkins.filter((checkin) => {
      const checkinDate = checkin.timestamp.toDate();
      return (
        checkinDate.getFullYear() === targetDate.getFullYear() &&
        checkinDate.getMonth() === targetDate.getMonth() &&
        checkinDate.getDate() === targetDate.getDate()
      );
    }).length;
  };

  // 조합 데이터 생성
  const getCombinedRouteData = async (reportDate: Date): Promise<CombinedRouteData[]> => {
    console.log('=== 보고서 데이터 조합 시작:', format(reportDate, 'yyyy-MM-dd') + ' ===');

    try {
      // 1. 기본 노선 데이터 조회
      const routesSnapshot = await getDocs(collection(db, 'routes'));
      const routes: RouteData[] = routesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as RouteData[];

      console.log('기본 노선 수:', routes.length);

      // 2. 운행 기록 조회 (해당 날짜)
      // Document ID 형식: 'YYYY-MM-DD_routeId'
      const operationDateStr = format(reportDate, 'yyyy-MM-dd');

      // operationDate 필드가 없으므로 Document ID 패턴으로 조회
      const allOperationsSnapshot = await getDocs(collection(db, 'operations'));

      // Document ID가 해당 날짜로 시작하는 것만 필터링
      const operations: Operation[] = allOperationsSnapshot.docs
        .filter(doc => doc.id.startsWith(operationDateStr))
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Operation[];

      console.log(`${operationDateStr} 운행 기록 수:`, operations.length);

      // 3. 체크인 데이터 조회 (해당 월)
      const yearMonth = format(reportDate, 'yyyyMM');
      const checkinsQuery = query(
        collection(db, 'route_monthly_checkins'),
        where('year_month', '==', yearMonth)
      );
      const checkinsSnapshot = await getDocs(checkinsQuery);
      const checkinsByRoute = new Map<string, CheckinRecord[]>();
      checkinsSnapshot.docs.forEach((doc) => {
        const data = doc.data() as RouteMonthlyCheckin;
        // routeId 추출: id 형식이 'routeId_YYYYMM'
        const routeId = doc.id.replace(`_${yearMonth}`, '');
        checkinsByRoute.set(routeId, data.checkins || []);
      });

      // 4. 수기입력 데이터 조회
      const manualCountsQuery = query(
        collection(db, 'manual_passenger_counts'),
        where('date', '==', Timestamp.fromDate(new Date(reportDate.setHours(0, 0, 0, 0))))
      );
      const manualCountsSnapshot = await getDocs(manualCountsQuery);
      const manualCounts = new Map<string, number>();
      manualCountsSnapshot.docs.forEach((doc) => {
        const data = doc.data() as ManualPassengerCount;
        manualCounts.set(data.routeId, data.manualCount);
      });

      // 5. 각 노선별 조합 데이터 생성
      const combined: CombinedRouteData[] = [];
      const reportWeekday = convertToFirebaseWeekday(reportDate);

      for (const route of routes) {
        // 노선 설정 조회
        const config = await getRouteConfigForDate(route.id, reportDate);

        // 최종 노선 정보 결정
        const finalName = config ? config.name : route.name;
        const finalDepartureTime = config ? config.departureTime : route.departureTime;
        const finalOperatingDays = config ? config.operatingDays : route.operatingDays || [];
        const dataSource: 'configuration' | 'route' = config ? 'configuration' : 'route';

        // 운영일 확인
        const isOperatingDay = finalOperatingDays.includes(reportWeekday);

        // 운행 기록 찾기
        const operation = operations.find((op) => op.routeId === route.id);

        // 체크인 이용인원 계산
        const checkins = checkinsByRoute.get(route.id) || [];
        const originalPassengerCount = getPassengerCount(checkins, reportDate);

        // 수기입력 이용인원
        const manualPassengerCount = manualCounts.get(route.id) ?? null;

        // 최종 이용인원
        const finalPassengerCount =
          manualPassengerCount !== null ? manualPassengerCount : originalPassengerCount;

        combined.push({
          routeId: route.id,
          routeName: finalName,
          departureTime: isOperatingDay ? finalDepartureTime : '[휴무일]',
          vehicleNumber: isOperatingDay ? operation?.vehicleNumber || '' : '[휴무일]',
          driverName: isOperatingDay ? operation?.driverName || '' : '[휴무일]',
          originalPassengerCount: isOperatingDay ? originalPassengerCount : 0,
          manualPassengerCount: isOperatingDay ? manualPassengerCount : null,
          finalPassengerCount: isOperatingDay ? finalPassengerCount : 0,
          isOperatingDay,
          dataSource,
          vehicleStatus: isOperatingDay ? '없음' : '[휴무일]',
          effectiveFrom: config ? config.effectiveFrom.toDate() : undefined,
        });
	      }

	      // 노선 출력 순서: 하단 → 강서구청 → 기타
	      const getRouteSortKey = (routeName: string) => {
	        const normalized = routeName.replace(/\s+/g, '');
	        const areaOrder = normalized.includes('하단')
	          ? 0
	          : normalized.includes('강서구청') || normalized.includes('강서')
	            ? 1
	            : 2;
	
	        const directionOrder = normalized.includes('출근')
	          ? 0
	          : normalized.includes('퇴근')
	            ? 1
	            : 2;
	
	        const lastNumberMatch = normalized.match(/(\d+)(?!.*\d)/);
	        const numberOrder = lastNumberMatch ? Number.parseInt(lastNumberMatch[1], 10) : 0;
	
	        return { areaOrder, directionOrder, numberOrder, normalized };
	      };
	
	      combined.sort((a, b) => {
	        const aKey = getRouteSortKey(a.routeName);
	        const bKey = getRouteSortKey(b.routeName);
	
	        if (aKey.areaOrder !== bKey.areaOrder) return aKey.areaOrder - bKey.areaOrder;
	        if (aKey.directionOrder !== bKey.directionOrder)
	          return aKey.directionOrder - bKey.directionOrder;
	        if (aKey.numberOrder !== bKey.numberOrder) return aKey.numberOrder - bKey.numberOrder;
	
	        return a.routeName.localeCompare(b.routeName, 'ko');
	      });

	      console.log('=== 보고서 데이터 조합 완료 ===');
	      return combined;
	    } catch (error) {
	      console.error('데이터 조합 오류:', error);
      return [];
    }
  };

  // 집계 계산
  const calculateSummary = (data: CombinedRouteData[]): PassengerSummary => {
    let hadan1Total = 0;
    let hadan2Total = 0;
    let gangseo1Total = 0;
    let gangseo2Total = 0;

    data.forEach((item) => {
      if (!item.isOperatingDay) return;

      const name = item.routeName;
      const count = item.finalPassengerCount;

      if (name.includes('하단') && name.includes('출근')) {
        hadan1Total += count;
      } else if (name.includes('하단') && name.includes('퇴근')) {
        hadan2Total += count;
      } else if (name.includes('강서') && name.includes('출근')) {
        gangseo1Total += count;
      } else if (name.includes('강서') && name.includes('퇴근')) {
        gangseo2Total += count;
      }
    });

    const totalPassengers = hadan1Total + hadan2Total + gangseo1Total + gangseo2Total;

    return {
      hadan1Total,
      hadan2Total,
      gangseo1Total,
      gangseo2Total,
      totalPassengers,
    };
  };

  // 데이터 로드
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await getCombinedRouteData(selectedDate);
        setCombinedData(data);
        setSummary(calculateSummary(data));
      } catch (error) {
        console.error('데이터 로드 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // 마이그레이션 상태 확인 (마운트 시)
  useEffect(() => {
    if (canAccessMigration()) {
      checkMigrationStatus();
    }
  }, []);

  // 수기입력 다이얼로그 열기
  const openManualInputDialog = (routeId: string, routeName: string, currentValue: number | null) => {
    setManualInputDialog({
      open: true,
      routeId,
      routeName,
      currentValue,
    });
    setManualInputValue(currentValue !== null ? currentValue.toString() : '');
  };

  // 수기입력 저장
  const saveManualInput = async () => {
    const { routeId } = manualInputDialog;
    const value = parseInt(manualInputValue, 10);

    if (isNaN(value) || value < 0) {
      alert('올바른 숫자를 입력해주세요.');
      return;
    }

    try {
      const dateStr = format(selectedDate, 'yyyyMMdd');
      const docId = `${routeId}_${dateStr}`;

      const data: Omit<ManualPassengerCount, 'id'> = {
        routeId,
        date: Timestamp.fromDate(new Date(selectedDate.setHours(0, 0, 0, 0))),
        manualCount: value,
        lastModified: Timestamp.now(),
      };

      await setDoc(doc(db, 'manual_passenger_counts', docId), data);

      setManualInputDialog({ open: false, routeId: '', routeName: '', currentValue: null });

      // 데이터 새로고침
      const newData = await getCombinedRouteData(selectedDate);
      setCombinedData(newData);
      setSummary(calculateSummary(newData));
    } catch (error) {
      console.error('수기입력 저장 오류:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  // 수기입력 삭제
  const deleteManualInput = async () => {
    const { routeId } = manualInputDialog;

    try {
      const dateStr = format(selectedDate, 'yyyyMMdd');
      const docId = `${routeId}_${dateStr}`;

      await deleteDoc(doc(db, 'manual_passenger_counts', docId));

      setManualInputDialog({ open: false, routeId: '', routeName: '', currentValue: null });

      // 데이터 새로고침
      const newData = await getCombinedRouteData(selectedDate);
      setCombinedData(newData);
      setSummary(calculateSummary(newData));
    } catch (error) {
      console.error('수기입력 삭제 오류:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // PDF 생성 (일별) - HTML to Canvas 방식으로 한글 지원
  const generatePDF = async (date: Date): Promise<Blob | null> => {
    try {
      const data = await getCombinedRouteData(date);
      const summaryData = calculateSummary(data);

      // HTML 테이블 생성
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.width = '800px';
      container.style.backgroundColor = 'white';
      container.style.padding = '20px';
      container.style.fontFamily = "'Noto Sans KR', 'Malgun Gothic', sans-serif";

      const dateStr = format(date, 'yyyy년 MM월 dd일', { locale: ko });
      const weekday = getKoreanWeekday(date);

      container.innerHTML = `
        <div style="text-align: center; margin-bottom: 15px;">
          <h1 style="font-size: 20px; font-weight: bold; margin-bottom: 8px;">차량운행일지</h1>
          <p style="font-size: 13px;">${dateStr} (${weekday})</p>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 10px;">
          <thead>
            <tr style="background-color: #f5f5f5; color: black;">
              <th style="border: 1px solid #000; padding: 6px; font-weight: bold;">코스</th>
              <th style="border: 1px solid #000; padding: 6px; font-weight: bold;">차량번호</th>
              <th style="border: 1px solid #000; padding: 6px; font-weight: bold;">승무원</th>
              <th style="border: 1px solid #000; padding: 6px; font-weight: bold;">출발시간</th>
              <th style="border: 1px solid #000; padding: 6px; font-weight: bold;">이용인원</th>
              <th style="border: 1px solid #000; padding: 6px; font-weight: bold;">차량이상유무</th>
            </tr>
          </thead>
          <tbody>
            ${data
              .map(
                (item) => `
              <tr style="background-color: ${item.isOperatingDay ? 'white' : '#f0f0f0'};">
                <td style="border: 1px solid #000; padding: 6px;">${item.routeName}</td>
                <td style="border: 1px solid #000; padding: 6px;">${item.vehicleNumber}</td>
                <td style="border: 1px solid #000; padding: 6px;">${item.driverName}</td>
                <td style="border: 1px solid #000; padding: 6px;">${item.departureTime}</td>
                <td style="border: 1px solid #000; padding: 6px; text-align: right;">${item.finalPassengerCount}명</td>
                <td style="border: 1px solid #000; padding: 6px;">${item.vehicleStatus}</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>

        <div style="margin-top: 15px;">
          <h3 style="font-size: 11px; font-weight: bold; margin-bottom: 8px;">이용인원 집계</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 9px;">
            <thead>
              <tr style="background-color: #f5f5f5;">
                <th style="border: 1px solid #000; padding: 4px; font-weight: bold;">구분</th>
                <th style="border: 1px solid #000; padding: 4px; font-weight: bold; text-align: right;">인원</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="border: 1px solid #000; padding: 4px;">하단 출근 총인원</td>
                <td style="border: 1px solid #000; padding: 4px; text-align: right; font-weight: bold;">${summaryData.hadan1Total}명</td>
              </tr>
              <tr>
                <td style="border: 1px solid #000; padding: 4px;">하단 퇴근 총인원</td>
                <td style="border: 1px solid #000; padding: 4px; text-align: right; font-weight: bold;">${summaryData.hadan2Total}명</td>
              </tr>
              <tr>
                <td style="border: 1px solid #000; padding: 4px;">강서 출근 총인원</td>
                <td style="border: 1px solid #000; padding: 4px; text-align: right; font-weight: bold;">${summaryData.gangseo1Total}명</td>
              </tr>
              <tr>
                <td style="border: 1px solid #000; padding: 4px;">강서 퇴근 총인원</td>
                <td style="border: 1px solid #000; padding: 4px; text-align: right; font-weight: bold;">${summaryData.gangseo2Total}명</td>
              </tr>
              <tr style="background-color: #f5f5f5;">
                <td style="border: 1px solid #000; padding: 4px; font-weight: bold;">총 이용인원</td>
                <td style="border: 1px solid #000; padding: 4px; text-align: right; font-weight: bold;">${summaryData.totalPassengers}명</td>
              </tr>
            </tbody>
          </table>
        </div>
      `;

      document.body.appendChild(container);

      // HTML을 Canvas로 변환
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      document.body.removeChild(container);

      // Canvas를 PDF로 변환
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // 첫 페이지 추가
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // 여러 페이지가 필요한 경우
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      return pdf.output('blob');
    } catch (error) {
      console.error('PDF 생성 오류:', error);
      return null;
    }
  };

  // 일별 PDF 다운로드
  const handleDownloadPDF = async () => {
    setPdfGenerating(true);
    try {
      const blob = await generatePDF(selectedDate);
      if (!blob) {
        alert('PDF 생성에 실패했습니다.');
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `차량운행일지_${format(selectedDate, 'yyyyMMdd')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF 다운로드 오류:', error);
      alert('PDF 다운로드 중 오류가 발생했습니다.');
    } finally {
      setPdfGenerating(false);
    }
  };

  // 월간 PDF ZIP 다운로드
  const handleDownloadMonthlyPDF = async () => {
    const firstDay = startOfMonth(selectedDate);
    const lastDay = endOfMonth(selectedDate);
    const allDays = eachDayOfInterval({ start: firstDay, end: lastDay });

    console.log(
      `=== 월간 보고서 생성 시작: ${format(selectedDate, 'yyyy년 MM월', { locale: ko })} ===`
    );

    setMonthlyPdfProgress({ open: true, current: 0, total: allDays.length });

    const zip = new JSZip();
    let successCount = 0;

    for (let i = 0; i < allDays.length; i++) {
      const date = allDays[i];
      try {
        const blob = await generatePDF(date);
        if (blob) {
          const filename = `차량운행일지_${format(date, 'yyyyMMdd')}.pdf`;
          zip.file(filename, blob);
          successCount++;
        }
      } catch (error) {
        console.error(`${format(date, 'yyyy-MM-dd')} PDF 생성 실패:`, error);
      }

      setMonthlyPdfProgress({ open: true, current: i + 1, total: allDays.length });
    }

    console.log(`총 ${successCount}개 PDF 파일 생성 완료`);

    // ZIP 다운로드
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `차량운행일지_${format(selectedDate, 'yyyyMM')}.zip`;
    link.click();
    URL.revokeObjectURL(url);

    setMonthlyPdfProgress({ open: false, current: 0, total: 0 });
    console.log('=== 월간 보고서 생성 및 다운로드 완료 ===');
  };

  return (
    <MainLayout>
      <Box>
        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Description sx={{ fontSize: 32, color: '#667eea' }} />
          <Typography variant="h4" component="h1" fontWeight={700}>
            차량운행일지
          </Typography>
        </Box>

        {/* 날짜 선택 카드 */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                <DatePicker
                  label="날짜 선택"
                  value={selectedDate}
                  onChange={(newValue) => newValue && setSelectedDate(newValue)}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>

              <Typography variant="h6" fontWeight={600}>
                {format(selectedDate, 'yyyy년 MM월 dd일', { locale: ko })} ({getKoreanWeekday(selectedDate)})
              </Typography>

              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<FolderZip />}
                  onClick={handleDownloadMonthlyPDF}
                  disabled={loading}
                >
                  이번달 전체 보고서 다운로드
                </Button>
              </Box>
            </Box>

            {/* 마이그레이션 버튼 (관리자 전용) */}
            {canAccessMigration() && migrationStatus && (
              <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                {migrationStatus.migrated === migrationStatus.total ? (
                  <Chip
                    icon={<CheckCircle />}
                    label={`데이터 마이그레이션 완료 (${migrationStatus.migrated}/${migrationStatus.total})`}
                    color="success"
                    sx={{ fontWeight: 600 }}
                  />
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<SyncAlt />}
                    onClick={() => setMigrationDialog(true)}
                    color="warning"
                  >
                    데이터 마이그레이션 ({migrationStatus.migrated}/{migrationStatus.total})
                  </Button>
                )}
                <Chip label="관리자 전용 기능" size="small" color="error" />
                <Typography variant="caption" color="text.secondary">
                  routes 컬렉션 데이터를 route_configurations로 복사합니다.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* PDF 다운로드 버튼 */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={pdfGenerating ? <CircularProgress size={20} color="inherit" /> : <CloudDownload />}
            onClick={handleDownloadPDF}
            disabled={loading || pdfGenerating}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)',
              },
            }}
          >
            {pdfGenerating ? 'PDF 생성 중...' : 'PDF 다운로드'}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* 운행일지 테이블 */}
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#667eea' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>코스</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>차량번호</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>승무원</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>출발시간</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>
                      이용인원
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>
                      이용인원(수기입력)
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>차량이상유무</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>데이터 출처</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {combinedData.map((item) => (
                    <TableRow
                      key={item.routeId}
                      sx={{
                        backgroundColor: item.isOperatingDay ? 'white' : '#f5f5f5',
                      }}
                    >
                      <TableCell>{item.routeName}</TableCell>
                      <TableCell>{item.vehicleNumber}</TableCell>
                      <TableCell>{item.driverName}</TableCell>
                      <TableCell>{item.departureTime}</TableCell>
                      <TableCell align="right">{item.originalPassengerCount}명</TableCell>
                      <TableCell align="right">
                        {item.isOperatingDay ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                backgroundColor:
                                  item.manualPassengerCount !== null ? '#e3f2fd' : 'transparent',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: item.manualPassengerCount !== null ? 600 : 400,
                              }}
                            >
                              {item.manualPassengerCount !== null ? `${item.manualPassengerCount}명` : '-'}
                            </Typography>
                            <Tooltip title="수기입력">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  openManualInputDialog(
                                    item.routeId,
                                    item.routeName,
                                    item.manualPassengerCount
                                  )
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="error" fontWeight={600}>
                            [휴무일]
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.vehicleStatus}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.dataSource === 'configuration' ? '설정' : '기본'}
                          size="small"
                          color={item.dataSource === 'configuration' ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* 데이터 출처 안내 */}
            <Card sx={{ mb: 3, backgroundColor: '#f9fafb' }}>
              <CardContent>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  데이터 출처 안내
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Chip label="설정" size="small" color="success" />
                  <Typography variant="body2" color="text.secondary">
                    해당 날짜의 특별 설정값 적용 (route_configurations)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Chip label="기본" size="small" color="default" />
                  <Typography variant="body2" color="text.secondary">
                    기본 노선 정보 사용 (routes)
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* 이용인원 집계 */}
            <Card sx={{ backgroundColor: '#f0f4ff' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  이용인원 집계 (수기입력 반영)
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      하단 출근 총인원
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {summary.hadan1Total}명
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      하단 퇴근 총인원
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {summary.hadan2Total}명
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      강서 출근 총인원
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {summary.gangseo1Total}명
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      강서 퇴근 총인원
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {summary.gangseo2Total}명
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 3, pt: 2, borderTop: '2px solid #667eea' }}>
                  <Typography variant="body2" color="text.secondary">
                    총 이용인원
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {summary.totalPassengers}명
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </>
        )}

        {/* 수기입력 다이얼로그 */}
        <Dialog
          open={manualInputDialog.open}
          onClose={() =>
            setManualInputDialog({ open: false, routeId: '', routeName: '', currentValue: null })
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            이용인원 수기입력
            <IconButton
              onClick={() =>
                setManualInputDialog({ open: false, routeId: '', routeName: '', currentValue: null })
              }
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {manualInputDialog.routeName}
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="이용인원"
              type="number"
              fullWidth
              value={manualInputValue}
              onChange={(e) => setManualInputValue(e.target.value)}
              inputProps={{ min: 0 }}
            />
            {manualInputDialog.currentValue !== null && (
              <Alert severity="info" sx={{ mt: 2 }}>
                현재 수기입력 값: {manualInputDialog.currentValue}명
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            {manualInputDialog.currentValue !== null && (
              <Button onClick={deleteManualInput} color="error">
                삭제
              </Button>
            )}
            <Button
              onClick={() =>
                setManualInputDialog({ open: false, routeId: '', routeName: '', currentValue: null })
              }
            >
              취소
            </Button>
            <Button onClick={saveManualInput} variant="contained">
              저장
            </Button>
          </DialogActions>
        </Dialog>

        {/* 마이그레이션 확인 다이얼로그 */}
        <Dialog open={migrationDialog} onClose={() => setMigrationDialog(false)}>
          <DialogTitle>데이터 마이그레이션</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              routes 컬렉션의 데이터를 route_configurations 컬렉션으로 복사합니다.
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              유효기간: 2025-01-01 ~ 2025-07-31
            </Typography>
            <Typography variant="body2" color="error">
              이미 마이그레이션된 노선은 건너뜁니다.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMigrationDialog(false)}>취소</Button>
            <Button onClick={executeMigration} variant="contained" disabled={migrating}>
              {migrating ? '마이그레이션 중...' : '실행'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 월간 PDF 생성 진행 다이얼로그 */}
        <Dialog open={monthlyPdfProgress.open} maxWidth="sm" fullWidth>
          <DialogTitle>월간 보고서 생성 중...</DialogTitle>
          <DialogContent>
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={(monthlyPdfProgress.current / monthlyPdfProgress.total) * 100}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {monthlyPdfProgress.current} / {monthlyPdfProgress.total} 일
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </MainLayout>
  );
};
